// @ts-check
import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Grid, ListItem, Typography, Box, IconButton, Divider } from '@material-ui/core';
import { formatNumberToUSD } from 'utils';
import { useCartContext } from 'modules/cart/contexts/CartContext';

import GiftOptionsButton from '../GiftOptionsButton';
import { PlusIcon, MinusIcon } from '../Icons';
import Image from '../Image';
import LinkButton from '../LinkButton';
import ItemInfo from '../ItemInfo';
import ProductPrice from '../ProductPrice';
import AssociatePinsSelectors from '../AssociatePinsSelectors';

const ProductItem = ({
  classes,
  item,
  currentQty,
  onItemRemove,
  isLastItem,
  editQty,
  adjustedPrice,
  onPriceAdjust,
  onGiftOptions,
  giftOptions,
  miniCartView,
  isProductForReservations,
  onReservationsItemRemove,
  userBrand,
  returnItemQty,
  isReturnItemChecked,
  isDividerHidden,
  showPimSku,
  hidePrice,
  itemPriceInDetails,
  hideQtyInItemDetails,
  validateAddQtyBtn,
  itemQtyEditAvailable,
}) => {
  const { productsMetaData } = useCartContext();

  const { pimSkuId, imageUrl = '', name, brandName, color, size, currentPrice, originalPrice, isGiftWrappable } = item;
  // Parsing price as per BE request
  const retailPrice = parseFloat(currentPrice || originalPrice || '0').toFixed(2);
  const _originalPrice = parseFloat(originalPrice || '0').toFixed(2);

  const ITEM_DETAILS_MAP = [
    {
      value: color,
      label: 'Color',
    },
    {
      value: size,
      label: 'Size',
    },
  ];

  if (miniCartView && !hideQtyInItemDetails) {
    ITEM_DETAILS_MAP.push({ value: currentQty?.toString(), label: 'Qty' });

    const itemPins = productsMetaData?.get(pimSkuId)?.associatePins ?? [];
    const checkedPins = itemPins.reduce((acc, item) => {
      if (!item.value) {
        return acc;
      }

      if (acc === '') {
        return `${item.pin}`;
      }

      return `${acc} | ${item.pin}`;
    }, '');

    ITEM_DETAILS_MAP.push({ value: checkedPins, label: 'Associates' });
  }
  const onRemoveClick = () => (isProductForReservations ? onReservationsItemRemove(pimSkuId) : onItemRemove(pimSkuId));

  const qtyForReservationProduct = item.currentQty || 1;

  const isMinusItemBtnAvailable = isProductForReservations ? qtyForReservationProduct !== 1 : currentQty !== 1;

  const isPlusItemBtnAvailable = validateAddQtyBtn ? currentQty < returnItemQty && isReturnItemChecked : true;

  return (
    <ListItem classes={{ root: classes.listItem }}>
      {!miniCartView && <AssociatePinsSelectors pimSkuId={item.pimSkuId} />}
      <Grid container className={cx(classes.container, miniCartView && '-withinMiniCart')}>
        <Grid item className={cx(classes.productImg, miniCartView && '-withinMiniCart')}>
          <Image src={imageUrl || ''} alt={brandName} userBrand={userBrand || 'BG'} data-testid="product-image" />
        </Grid>
        <Grid item className={cx(classes.details, miniCartView && '-withinMiniCart')}>
          {brandName && <Typography color="textSecondary">{brandName.toUpperCase()}</Typography>}
          <Typography className={classes.name}>
            {name || (isProductForReservations && 'Details unavailable')}
          </Typography>
          {showPimSku && (
            <Typography color="textSecondary" className={classes.paddingBottom}>
              PIM SKU {pimSkuId}
            </Typography>
          )}
          {itemPriceInDetails && !hidePrice && (
            <Typography className={classes.paddingBottom}>{formatNumberToUSD(currentPrice)}</Typography>
          )}
          <ItemInfo itemProperties={ITEM_DETAILS_MAP} alignValuesToLeft={isProductForReservations} />
          {(!miniCartView || itemQtyEditAvailable) && (
            <Grid className={classes.qtyContainer}>
              <Box className={classes.itemCount}>
                <IconButton
                  className={cx(isMinusItemBtnAvailable || classes.disabledBtn, classes.counterBtn)}
                  onClick={() => editQty(item, 'decrease')}
                  disabled={!isMinusItemBtnAvailable}
                >
                  <MinusIcon />
                </IconButton>
                <Typography className={classes.black}>
                  {isProductForReservations ? qtyForReservationProduct : currentQty}
                </Typography>
                <IconButton
                  className={cx(isPlusItemBtnAvailable || classes.disabledBtn, classes.counterBtn)}
                  onClick={() => editQty(item, 'increase')}
                  disabled={!isPlusItemBtnAvailable}
                >
                  <PlusIcon />
                </IconButton>
              </Box>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid container className={cx(classes.priceContainer, miniCartView && '-withinMiniCart')}>
        <Box className={cx(miniCartView ? classes.priceBoxLeftCell : classes.giftOptions)}>
          {!isProductForReservations && (
            <GiftOptionsButton
              giftOptions={giftOptions}
              onClick={() => onGiftOptions(pimSkuId)}
              isProductWrappable
              isOrderWrappable={isGiftWrappable}
              hideGiftOptions={miniCartView}
            />
          )}
        </Box>
        {!miniCartView && (
          <Box className={cx(classes.priceBoxLeftCell, !miniCartView && '-withinMiniCart')}>
            <LinkButton classes={{ root: classes.linkButton, label: classes.linkButtonLabel }} onClick={onRemoveClick}>
              Remove
            </LinkButton>
          </Box>
        )}
        <Box className={cx(classes.priceBoxRightCell, !miniCartView && '-withinMiniCart')}>
          {retailPrice && !itemPriceInDetails && !hidePrice && (
            <>
              {!miniCartView && !isProductForReservations && (
                <LinkButton
                  classes={{ root: classes.linkButton, label: classes.adjustLabel }}
                  onClick={() => onPriceAdjust(pimSkuId, name, retailPrice)}
                >
                  {adjustedPrice ? 'Edit' : 'Adjust'}
                </LinkButton>
              )}
              <ProductPrice
                currencyCode="$"
                adjustedPrice={adjustedPrice}
                retailPrice={retailPrice}
                originalPrice={_originalPrice}
              />
            </>
          )}
        </Box>
      </Grid>
      {!isLastItem && !isDividerHidden && <Divider variant="middle" className={classes.divider} />}
    </ListItem>
  );
};

const ProductItemType = PropTypes.shape({
  pimSkuId: PropTypes.string,
  imageUrl: PropTypes.string,
  name: PropTypes.string,
  brandName: PropTypes.string,
  color: PropTypes.string,
  size: PropTypes.string,
  actualPrice: PropTypes.string,
  availableQuantity: PropTypes.string,
});

ProductItem.propTypes = {
  classes: PropTypes.shape({}),
  item: ProductItemType,
  isLastItem: PropTypes.bool,
  onItemRemove: PropTypes.func,
  currentQty: PropTypes.number,
  editQty: PropTypes.func,
  adjustedPrice: PropTypes.string,
  onPriceAdjust: PropTypes.func,
  onGiftOptions: PropTypes.func,
  giftOptions: PropTypes.shape({}),
  miniCartView: PropTypes.bool,
  isProductForReservations: PropTypes.bool,
  onReservationsItemRemove: PropTypes.func,
  userBrand: PropTypes.string,
  returnItemQty: PropTypes.number,
  isReturnItemChecked: PropTypes.bool,
  isDividerHidden: PropTypes.bool,
  showPimSku: PropTypes.bool,
  hidePrice: PropTypes.bool,
  itemPriceInDetails: PropTypes.bool,
  hideQtyInItemDetails: PropTypes.bool,
  validateAddQtyBtn: PropTypes.bool,
  itemQtyEditAvailable: PropTypes.bool,
};

ProductItem.defaultProps = {
  classes: {},
  item: {},
  miniCartView: false,
  isProductForReservations: false,
  isDividerHidden: false,
};

export default ProductItem;
